/**
 * Main App Component with Routing
 */

import { Suspense, lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { getApiErrorMessage } from '@/lib/utils';
import { BRAND } from '@/config/brand';
import { useAuth } from '@/hooks';
import { MainLayout } from '@/components/layouts/MainLayout';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { PortalLayout } from '@/components/layouts/PortalLayout';
import { LoadingOverlay, LoadingSpinner } from '@/components/ui';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { PortalOnly } from '@/components/auth/PortalOnly';
import { StudioOnly } from '@/components/auth/StudioOnly';

/** Retry lazy import; on chunk load failure (e.g. after deploy), reload to fetch fresh assets. */
function lazyWithRetry<T extends ComponentType<any>>(importFn: () => Promise<{ default: T }>, retries = 2): LazyExoticComponent<T> {
  return lazy(async () => {
    const load = (remaining: number): Promise<{ default: T }> =>
      importFn().catch((err) => {
        const msg = getApiErrorMessage(err, '');
        const isChunkError = /Failed to fetch dynamically imported module|Loading chunk \d+ failed/i.test(msg);
        if (remaining > 0 && isChunkError) {
          return load(remaining - 1);
        }
        window.location.reload();
        throw err;
      });
    return load(retries);
  });
}

// Lazy loaded pages

/** Landing page after sign-in (the studio's Today overview). */
const HOME_PATH = '/today';
const LoginPage = lazyWithRetry(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazyWithRetry(() => import('@/pages/auth/VerifyEmailPage'));
const RegisterPage = lazyWithRetry(() => import('@/pages/auth/RegisterPage'));
const DashboardPage = lazyWithRetry(() => import('@/pages/dashboard/DashboardPage'));
const UsersPage = lazyWithRetry(() => import('@/pages/admin/system/UsersPage'));
const RolesPage = lazyWithRetry(() => import('@/pages/admin/system/RolesPage'));
const ApiKeysPage = lazyWithRetry(() => import('@/pages/admin/system/ApiKeysPage'));
const AuditLogsPage = lazyWithRetry(() => import('@/pages/admin/audit/AuditLogsPage'));
const ClientsPage = lazyWithRetry(() => import('@/pages/clients/ClientsPage'));
const ClientDetailPage = lazyWithRetry(() => import('@/pages/clients/ClientDetailPage'));
const ProjectsPage = lazyWithRetry(() => import('@/pages/projects/ProjectsPage'));
const ProjectDetailPage = lazyWithRetry(() => import('@/pages/projects/ProjectDetailPage'));
const InvoicesPage = lazyWithRetry(() => import('@/pages/invoices/InvoicesPage'));
const PayPage = lazyWithRetry(() => import('@/pages/public/PayPage'));
const PrivacyPage = lazyWithRetry(() => import('@/pages/public/PrivacyPage'));
const InvoiceDetailPage = lazyWithRetry(() => import('@/pages/invoices/InvoiceDetailPage'));
const ProposalsPage = lazyWithRetry(() => import('@/pages/proposals/ProposalsPage'));
const PaymentsPage = lazyWithRetry(() => import('@/pages/payments/PaymentsPage'));
const PaymentDetailPage = lazyWithRetry(() => import('@/pages/payments/PaymentDetailPage'));
const BankingPage = lazyWithRetry(() => import('@/pages/banking/BankingPage'));
const PurchasesPage = lazyWithRetry(() => import('@/pages/purchases/PurchasesPage'));
const PurchaseDetailPage = lazyWithRetry(() => import('@/pages/purchases/PurchaseDetailPage'));
const ReceiptsPage = lazyWithRetry(() => import('@/pages/purchases/ReceiptsPage'));
const ReceiptReviewPage = lazyWithRetry(() => import('@/pages/purchases/ReceiptReviewPage'));
const ExpensesPage = lazyWithRetry(() => import('@/pages/expenses/ExpensesPage'));
const ExpenseDetailPage = lazyWithRetry(() => import('@/pages/expenses/ExpenseDetailPage'));
const OwnerAccountPage = lazyWithRetry(() => import('@/pages/owner/OwnerAccountPage'));
const ReportsPage = lazyWithRetry(() => import('@/pages/reports/ReportsPage'));
const ProjectsBoardPage = lazyWithRetry(() => import('@/pages/projects/ProjectsBoardPage'));
const TodayPage = lazyWithRetry(() => import('@/pages/workspace/TodayPage'));
const AlertsPage = lazyWithRetry(() => import('@/pages/alerts/AlertsPage'));
const TasksPage = lazyWithRetry(() => import('@/pages/workspace/TasksPage'));
const CalendarPage = lazyWithRetry(() => import('@/pages/workspace/CalendarPage'));
const StudioSettingsPage = lazyWithRetry(() => import('@/pages/settings/StudioSettingsPage'));
const SettingsPage = lazyWithRetry(() => import('@/pages/settings/SettingsPage'));
const DocsPage = lazyWithRetry(() => import('@/pages/docs/DocsPage'));
const ForbiddenPage = lazyWithRetry(() => import('@/pages/ForbiddenPage'));
const NotFoundPage = lazyWithRetry(() => import('@/pages/NotFoundPage'));
const PortalHomePage = lazyWithRetry(() => import('@/pages/portal/PortalHomePage'));
const PortalProjectPage = lazyWithRetry(() => import('@/pages/portal/PortalProjectPage'));
const PortalInvoicesPage = lazyWithRetry(() => import('@/pages/portal/PortalInvoicesPage'));
const PortalApprovalsPage = lazyWithRetry(() => import('@/pages/portal/PortalApprovalsPage'));
const PortalApprovalPage = lazyWithRetry(() => import('@/pages/portal/PortalApprovalPage'));
const PortalDocumentsPage = lazyWithRetry(() => import('@/pages/portal/PortalDocumentsPage'));
const OAuthConsentPage = lazyWithRetry(() => import('@/pages/oauth/OAuthConsentPage'));

function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingOverlay open title={`Loading ${BRAND.name}...`} description="Preparing your workspace." />;
  }

  return (
    <Suspense
      fallback={
        <div
          className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <LoadingSpinner size="lg" />
          <div className="text-base font-semibold text-foreground">Loading…</div>
          <div className="text-sm text-muted-foreground">Fetching the next screen.</div>
        </div>
      }
    >
      <Routes location={location} key={location.pathname}>
        {/* Public client pages (no login; the pay token is the credential) */}
        <Route path="/pay/:token" element={<PayPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* OAuth consent for AI apps (signed-in owner, no studio shell) */}
        <Route
          path="/oauth/consent"
          element={
            <ProtectedRoute requiredPermission="integrations.manage">
              <OAuthConsentPage />
            </ProtectedRoute>
          }
        />

        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={isAuthenticated ? <Navigate to={HOME_PATH} replace /> : <LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
        </Route>

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <StudioOnly>
                <MainLayout />
              </StudioOnly>
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to={HOME_PATH} replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Admin */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredPermission="users.view">
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute requiredPermission="roles.view">
                <RolesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/api-keys"
            element={
              <ProtectedRoute requiredPermission="admin.api_keys">
                <ApiKeysPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <ProtectedRoute requiredPermission="audit.view">
                <AuditLogsPage />
              </ProtectedRoute>
            }
          />
          {/* Studio */}
          <Route
            path="/clients"
            element={
              <ProtectedRoute requiredPermission="clients.view">
                <ClientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <ProtectedRoute requiredPermission="clients.view">
                <ClientDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute requiredPermission="projects.view">
                <ProjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/board"
            element={
              <ProtectedRoute requiredPermission="projects.view">
                <ProjectsBoardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute requiredPermission="projects.view">
                <ProjectDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/proposals"
            element={
              <ProtectedRoute requiredPermission="proposals.view">
                <ProposalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute requiredPermission="invoices.view">
                <InvoicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute requiredPermission="invoices.view">
                <InvoiceDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute requiredPermission="payments.view">
                <PaymentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/:id"
            element={
              <ProtectedRoute requiredPermission="payments.view">
                <PaymentDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/banking"
            element={
              <ProtectedRoute requiredPermission="banking.view">
                <BankingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchases"
            element={
              <ProtectedRoute requiredPermission="purchases.view">
                <PurchasesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchases/:id"
            element={
              <ProtectedRoute requiredPermission="purchases.view">
                <PurchaseDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchases/receipts"
            element={
              <ProtectedRoute requiredPermission="purchases.view">
                <ReceiptsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchases/receipts/:id"
            element={
              <ProtectedRoute requiredPermission="purchases.view">
                <ReceiptReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <ProtectedRoute requiredPermission="expenses.view">
                <ExpensesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/:id"
            element={
              <ProtectedRoute requiredPermission="expenses.view">
                <ExpenseDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/owner"
            element={
              <ProtectedRoute requiredPermission="owner.view">
                <OwnerAccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute requiredPermission="reports.view">
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <ProtectedRoute requiredPermission="alerts.view">
                <AlertsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/today"
            element={
              <ProtectedRoute requiredPermission="projects.view">
                <TodayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute requiredPermission="projects.view">
                <TasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute requiredPermission="projects.view">
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          {/* Settings */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/settings/studio"
            element={
              <ProtectedRoute requiredPermission="business.settings">
                <StudioSettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Developer Documentation (Admin/Dev only) */}
          <Route
            path="/docs"
            element={
              <ProtectedRoute requiredRole="admin">
                <DocsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/:docId"
            element={
              <ProtectedRoute requiredRole="admin">
                <DocsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Client portal (role "client" only; studio users go to Today) */}
        <Route
          element={
            <ProtectedRoute>
              <PortalOnly>
                <ProtectedRoute requiredPermission="portal.access">
                  <PortalLayout />
                </ProtectedRoute>
              </PortalOnly>
            </ProtectedRoute>
          }
        >
          <Route path="/portal" element={<PortalHomePage />} />
          <Route path="/portal/projects/:id" element={<PortalProjectPage />} />
          <Route path="/portal/invoices" element={<PortalInvoicesPage />} />
          <Route path="/portal/approvals" element={<PortalApprovalsPage />} />
          <Route path="/portal/approvals/:id" element={<PortalApprovalPage />} />
          <Route path="/portal/documents" element={<PortalDocumentsPage />} />
        </Route>

        {/* Forbidden */}
        <Route path="/forbidden" element={<ForbiddenPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
